export const PLATFORM_FAVICON_SETTING_KEY = 'platformFaviconCustomization'

export const MAX_FAVICON_SVG_SIZE = 128 * 1024

export const DEFAULT_PLATFORM_FAVICON_CUSTOMIZATION = {
  version: 1,
  svg: '',
  fileName: '',
  color: '#8d3ed8',
  fit: 'contain',
  showUnreadCount: false,
}

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'
const FAVICON_FITS = ['contain', 'cover', 'fill']
const FORBIDDEN_SVG_ELEMENTS = new Set([
  'a',
  'animate',
  'animatemotion',
  'animatetransform',
  'audio',
  'discard',
  'feimage',
  'script',
  'foreignobject',
  'handler',
  'embed',
  'object',
  'iframe',
  'image',
  'link',
  'meta',
  'set',
  'style',
  'video',
])

const getUtf8ByteLength = (value) => {
  let length = 0

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)

    if (code <= 0x7f) {
      length += 1
    } else if (code <= 0x7ff) {
      length += 2
    } else if (
      code >= 0xd800 &&
      code <= 0xdbff &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      length += 4
      index += 1
    } else {
      length += 3
    }
  }

  return length
}

const isLocalSvgReference = (value) => value.trim().startsWith('#')

const hasExternalUrlReference = (value) => {
  const matches = value.match(/url\s*\(\s*(['"]?)(.*?)\1\s*\)/gi)
  if (!matches) return false

  return matches.some((match) => {
    const reference = match
      .replace(/^url\s*\(\s*/i, '')
      .replace(/\s*\)$/i, '')
      .replace(/^(['"])(.*)\1$/, '$2')

    return !isLocalSvgReference(reference)
  })
}

const sanitizeSvgAttributes = (element) => {
  Array.from(element.attributes).forEach((attribute) => {
    const name = attribute.name.toLowerCase()
    const localName = attribute.localName.toLowerCase()

    if (
      name.startsWith('on') ||
      name === 'style' ||
      name === 'xml:base' ||
      (localName === 'href' && !isLocalSvgReference(attribute.value)) ||
      hasExternalUrlReference(attribute.value)
    ) {
      element.removeAttributeNode(attribute)
    }
  })
}

export const sanitizeFaviconSvg = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error('SVG-файл пустой')
  }

  if (getUtf8ByteLength(value) > MAX_FAVICON_SVG_SIZE) {
    throw new Error('SVG-файл превышает лимит 128 КБ')
  }

  if (/<!\s*(?:doctype|entity)\b/i.test(value)) {
    throw new Error('SVG содержит неподдерживаемые декларации')
  }

  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    throw new Error('SVG-санитизация недоступна в текущем окружении')
  }

  const document = new DOMParser().parseFromString(value, 'image/svg+xml')
  const root = document.documentElement

  if (
    document.querySelector('parsererror') ||
    !root ||
    root.localName.toLowerCase() !== 'svg' ||
    (root.namespaceURI && root.namespaceURI !== SVG_NAMESPACE)
  ) {
    throw new Error('Некорректный SVG-файл')
  }

  Array.from(root.getElementsByTagName('*')).forEach((element) => {
    if (FORBIDDEN_SVG_ELEMENTS.has(element.localName.toLowerCase())) {
      element.remove()
    }
  })

  const svgElements = [root, ...Array.from(root.getElementsByTagName('*'))]
  svgElements.forEach(sanitizeSvgAttributes)

  if (!root.getAttribute('xmlns')) root.setAttribute('xmlns', SVG_NAMESPACE)

  const viewBox = root
    .getAttribute('viewBox')
    ?.trim()
    .split(/[\s,]+/)
    .map(Number)
  if (
    viewBox?.length === 4 &&
    Number.isFinite(viewBox[2]) &&
    Number.isFinite(viewBox[3]) &&
    viewBox[2] > 0 &&
    viewBox[3] > 0
  ) {
    const width = root.getAttribute('width')
    const height = root.getAttribute('height')

    if (!width || width.includes('%')) root.setAttribute('width', viewBox[2])
    if (!height || height.includes('%')) root.setAttribute('height', viewBox[3])
  }

  const sanitizedSvg = new XMLSerializer().serializeToString(root)
  if (getUtf8ByteLength(sanitizedSvg) > MAX_FAVICON_SVG_SIZE) {
    throw new Error('SVG-файл превышает лимит 128 КБ')
  }

  return sanitizedSvg
}

export const isValidFaviconColor = (value) =>
  typeof value === 'string' &&
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)

export const normalizeFaviconFit = (value) =>
  FAVICON_FITS.includes(value)
    ? value
    : DEFAULT_PLATFORM_FAVICON_CUSTOMIZATION.fit

const getParsedSetting = (setting) => {
  if (!setting) return null

  try {
    const value = typeof setting === 'string' ? JSON.parse(setting) : setting
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : null
  } catch {
    return null
  }
}

export const parsePlatformFaviconCustomization = (setting) => {
  const value = getParsedSetting(setting)
  if (!value) return { ...DEFAULT_PLATFORM_FAVICON_CUSTOMIZATION }

  let svg = ''
  if (typeof value.svg === 'string' && value.svg.trim()) {
    try {
      svg = sanitizeFaviconSvg(value.svg)
    } catch {
      svg = ''
    }
  }

  const color = isValidFaviconColor(value.color)
    ? value.color.toLowerCase()
    : DEFAULT_PLATFORM_FAVICON_CUSTOMIZATION.color

  return {
    version:
      Number.isInteger(value.version) && value.version > 0
        ? value.version
        : DEFAULT_PLATFORM_FAVICON_CUSTOMIZATION.version,
    svg,
    fileName: typeof value.fileName === 'string' ? value.fileName.trim() : '',
    color,
    fit: normalizeFaviconFit(value.fit),
    showUnreadCount: value.showUnreadCount === true,
  }
}

export const getFaviconDrawRect = (
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
  fit = DEFAULT_PLATFORM_FAVICON_CUSTOMIZATION.fit,
) => {
  if (
    !Number.isFinite(targetWidth) ||
    !Number.isFinite(targetHeight) ||
    targetWidth <= 0 ||
    targetHeight <= 0
  ) {
    return { x: 0, y: 0, width: 0, height: 0 }
  }

  if (
    normalizeFaviconFit(fit) === 'fill' ||
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return { x: 0, y: 0, width: targetWidth, height: targetHeight }
  }

  const scale =
    normalizeFaviconFit(fit) === 'cover'
      ? Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight)
      : Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight)
  const width = sourceWidth * scale
  const height = sourceHeight * scale

  return {
    x: (targetWidth - width) / 2,
    y: (targetHeight - height) / 2,
    width,
    height,
  }
}

export const formatUnreadCount = (value) => {
  const count = Number(value)
  if (!Number.isFinite(count) || count <= 0) return ''

  const roundedCount = Math.floor(count)
  if (roundedCount <= 0) return ''

  return roundedCount > 99 ? '99+' : String(roundedCount)
}

const drawRoundedRect = (context, x, y, width, height, radius) => {
  context.beginPath()
  context.moveTo(x + radius, y)
  context.lineTo(x + width - radius, y)
  context.quadraticCurveTo(x + width, y, x + width, y + radius)
  context.lineTo(x + width, y + height - radius)
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius,
    y + height,
  )
  context.lineTo(x + radius, y + height)
  context.quadraticCurveTo(x, y + height, x, y + height - radius)
  context.lineTo(x, y + radius)
  context.quadraticCurveTo(x, y, x + radius, y)
  context.closePath()
}

const drawUnreadBadge = (context, size, label) => {
  const badgeHeight = Math.max(10, Math.round(size * 0.38))
  const badgeWidth = label.length > 2
    ? Math.round(badgeHeight * 1.45)
    : badgeHeight
  const x = size - badgeWidth
  const y = 0

  context.save()
  drawRoundedRect(context, x, y, badgeWidth, badgeHeight, badgeHeight / 2)
  context.fillStyle = '#f04438'
  context.fill()
  context.fillStyle = '#ffffff'
  context.font = `700 ${Math.max(7, Math.round(badgeHeight * 0.5))}px Arial, sans-serif`
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillText(label, x + badgeWidth / 2, y + badgeHeight / 2)
  context.restore()
}

const normalizeCanvasSize = (value) => {
  const size = Number(value)
  if (!Number.isFinite(size) || size <= 0) return 64

  return Math.min(512, Math.max(16, Math.round(size)))
}

export const generateFaviconDataUrl = async (
  setting,
  unreadCount = 0,
  size = 64,
) => {
  if (
    typeof document === 'undefined' ||
    typeof Image === 'undefined' ||
    typeof Blob === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return null
  }

  const customization = parsePlatformFaviconCustomization(setting)
  if (!customization.svg) return null

  let objectUrl = null

  try {
    const faviconSize = normalizeCanvasSize(size)
    const canvas = document.createElement('canvas')
    canvas.width = faviconSize
    canvas.height = faviconSize

    const context = canvas.getContext('2d')
    if (!context) return null

    const image = new Image()
    objectUrl = URL.createObjectURL(
      new Blob([customization.svg], { type: 'image/svg+xml' }),
    )

    await new Promise((resolve, reject) => {
      image.onload = resolve
      image.onerror = () => reject(new Error('Не удалось загрузить SVG'))
      image.src = objectUrl
    })

    const sourceWidth = image.naturalWidth || image.width || faviconSize
    const sourceHeight = image.naturalHeight || image.height || faviconSize
    const rect = getFaviconDrawRect(
      sourceWidth,
      sourceHeight,
      faviconSize,
      faviconSize,
      customization.fit,
    )

    context.clearRect(0, 0, faviconSize, faviconSize)
    context.save()
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height)
    context.globalCompositeOperation = 'source-in'
    context.fillStyle = customization.color
    context.fillRect(0, 0, faviconSize, faviconSize)
    context.restore()

    const unreadLabel = customization.showUnreadCount
      ? formatUnreadCount(unreadCount)
      : ''
    if (unreadLabel) drawUnreadBadge(context, faviconSize, unreadLabel)

    const dataUrl = canvas.toDataURL('image/png')
    return dataUrl.startsWith('data:image/png') ? dataUrl : null
  } catch {
    return null
  } finally {
    if (objectUrl && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(objectUrl)
    }
  }
}
